import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ConfigAPI from '../../../api/ConfigAPI';
import { ButtonV2 } from '../../../components/ButtonV2/ButtonV2';
import { CardContent, CardFooter, CardHeader, CardTitle, CardDescription, CardV2 } from '../../../components/CardV2/CardV2';
import { LabelV2 } from '../../../components/LabelV2/LabelV2';
import PrimaryButton from '../../../components/PrimaryButton/PrimaryButton';
import Switch from '../../../components/Switch/Switch';
import CONFIG from '../../../config/config';
import { toastService } from '../../../services/ToastService';
import { updateSettings } from '../../../store/reducers/AccountSlice';
import { getPersistedEnabledModules, TENANT_MODULES, TenantModule } from '../../../shared/tenantModules';

const normalizeEnabledModules = (enabledModules?: TenantModule[]) => {
  if (!enabledModules || enabledModules.length === 0) {
    return [...TENANT_MODULES];
  }

  return TENANT_MODULES.filter(module => enabledModules.includes(module));
};

export default function ModulesSegment() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [enabledModules, setEnabledModules] = useState<TenantModule[]>([...TENANT_MODULES]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    const fetchModules = async () => {
      setIsLoading(true);
      try {
        const response = await ConfigAPI.get();
        setEnabledModules(normalizeEnabledModules(response.data.enabledModules));
      } catch (error) {
        console.error('Failed to fetch modules configuration:', error);
        toastService.showToast(t('settings.modules.toast.load_error'), 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchModules();
  }, [t]);

  const toggleModule = (module: TenantModule) => {
    const isEnabled = enabledModules.includes(module);
    if (isEnabled && enabledModules.length === 1) {
      toastService.showToast(t('settings.modules.errors.at_least_one'), 'error');
      return;
    }

    setEnabledModules(current => (
      current.includes(module)
        ? current.filter(item => item !== module)
        : [...current, module]
    ));
    setHasChanged(true);
  };

  const saveModules = async () => {
    setIsLoading(true);
    try {
      const persistedModules = getPersistedEnabledModules(enabledModules);
      const response = await ConfigAPI.updateModules({ enabledModules: persistedModules });
      setEnabledModules(normalizeEnabledModules(response.data.enabledModules));
      dispatch(updateSettings({ enabledModules: response.data.enabledModules }));
      setHasChanged(false);
      toastService.showToast(t('settings.modules.toast.saved'), 'success');
    } catch (error) {
      console.error('Failed to update modules configuration:', error);
      toastService.showToast(t('settings.modules.toast.error'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto space-y-6">
      <CardV2>
        <CardHeader>
          <CardTitle>{t('settings.modules.title')}</CardTitle>
          <CardDescription>{t('settings.modules.description')}</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TENANT_MODULES.map(module => (
            <div key={module} className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-muted p-4">
              <div className="space-y-2">
                <LabelV2 htmlFor={`module-${module}`} className="text-base font-semibold text-foreground">
                  {t(`settings.modules.${module}.title`)}
                </LabelV2>
                <p className="text-sm text-muted-foreground">
                  {t(`settings.modules.${module}.description`)}
                </p>
                <p className="text-xs font-medium text-muted-foreground">
                  {enabledModules.includes(module) ? t('global.enabled') : t('global.disabled')}
                </p>
              </div>
              <Switch
                id={`module-${module}`}
                checked={enabledModules.includes(module)}
                onChange={() => toggleModule(module)}
                disabled={isLoading}
              />
            </div>
          ))}
        </CardContent>
        <CardFooter className="justify-center">
          <PrimaryButton
            text={t('global.save')!}
            fn={saveModules}
            isDisabled={isLoading || !hasChanged}
            isLoading={isLoading}
            actionName="Save Store Modules"
          />
        </CardFooter>
      </CardV2>

      <CardV2>
        <CardHeader>
          <CardTitle>{t('settings.modules.support.title')}</CardTitle>
          <CardDescription>{t('settings.modules.support.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <ButtonV2
            type="button"
            variant="outline"
            onClick={() => window.open(CONFIG.CONTACT_INFO.WHATSAPP, '_blank', 'noopener,noreferrer')}
            actionName="Contact Support Modules"
          >
            {t('settings.modules.support.cta')}
          </ButtonV2>
        </CardContent>
      </CardV2>
    </div>
  );
}
